import { baseApi } from '../baseApi';
import { API_PATHS } from '@/constants';
import { getActiveAccount } from '@/services';
import type {
  ApiResponse,
  PaginatedResponse,
  SpaceId,
  BuildingId,
  PlannerAccessId,
  SpaceWithAvailability,
  GetSpacesParams,
  CheckSpaceAvailabilityRequest,
  CheckSpaceAvailabilityResponse,
  CountryCode,
  Building,
  GetBuildingsParams,
  Region,
  PlannerBuildingAccess,
} from '@store/models';

export const spaceConfigServiceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSpaces: builder.query<PaginatedResponse<SpaceWithAvailability>, GetSpacesParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          const { buildingIds, ...rest } = params;
          Object.entries(rest).forEach(([key, value]) => {
            if (value !== undefined) {
              searchParams.set(key, String(value));
            }
          });
          if (Array.isArray(buildingIds) && buildingIds.length) {
            for (const id of buildingIds) {
              searchParams.append('buildingId', id);
            }
          }
        }
        const qs = searchParams.toString();
        return qs ? `${API_PATHS.SPACES}?${qs}` : API_PATHS.SPACES;
      },
      providesTags: ['Spaces'],
    }),

    searchSpaces: builder.query<
      ApiResponse<SpaceWithAvailability[]>,
      {
        buildingIds: string[];
        startDate?: string;
        endDate?: string;
        startTime?: string;
        endTime?: string;
      }
    >({
      query: ({ buildingIds, startDate, endDate, startTime, endTime }) => {
        const body: Record<string, unknown> = { buildingIds };
        if (startDate) body.startDate = startDate;
        if (endDate) body.endDate = endDate;
        if (startTime) body.startTime = startTime;
        if (endTime) body.endTime = endTime;
        return {
          url: API_PATHS.SPACES_SEARCH,
          method: 'POST',
          body,
        };
      },
      providesTags: ['Spaces'],
    }),

    searchSpacesAvailability: builder.mutation<
      ApiResponse<SpaceWithAvailability[]>,
      {
        buildingIds: string[];
        startDate?: string;
        endDate?: string;
        startTime?: string;
        endTime?: string;
      }
    >({
      query: ({ buildingIds, startDate, endDate, startTime, endTime }) => {
        const body: Record<string, unknown> = { buildingIds };
        if (startDate) body.startDate = startDate;
        if (endDate) body.endDate = endDate;
        if (startTime) body.startTime = startTime;
        if (endTime) body.endTime = endTime;
        return {
          url: API_PATHS.SPACES_SEARCH,
          method: 'POST',
          body,
        };
      },
    }),

    checkSpaceAvailability: builder.mutation<
      CheckSpaceAvailabilityResponse,
      { spaceId: SpaceId; body: CheckSpaceAvailabilityRequest }
    >({
      query: ({ spaceId, body }) => ({
        url: `${API_PATHS.SPACES}/${spaceId}/availability`,
        method: 'POST',
        body,
      }),
    }),

    getCountryCodes: builder.query<ApiResponse<CountryCode[]>, void>({
      query: () => API_PATHS.COUNTRIES,
      providesTags: ['Buildings'],
    }),

    getBuildings: builder.query<ApiResponse<Building[]>, GetBuildingsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              searchParams.set(key, String(value));
            }
          });
        }
        const qs = searchParams.toString();
        return qs ? `${API_PATHS.BUILDINGS}?${qs}` : API_PATHS.BUILDINGS;
      },
      providesTags: ['Buildings'],
    }),

    getBuildingById: builder.query<ApiResponse<Building>, BuildingId>({
      query: (id) => `${API_PATHS.BUILDINGS}/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Buildings', id }],
    }),

    getRegionConfig: builder.query<ApiResponse<Region>, string>({
      query: (regionName) => `${API_PATHS.REGIONS}/${regionName}`,
      providesTags: (_result, _error, regionName) => [{ type: 'Regions', id: regionName }],
    }),

    getPlannerAccess: builder.query<
      ApiResponse<PlannerBuildingAccess[]>,
      { plannerGroup?: string } | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.plannerGroup !== undefined) {
          searchParams.set('plannerGroup', params.plannerGroup);
        }
        const qs = searchParams.toString();
        return qs ? `${API_PATHS.PLANNER_ACCESS}?${qs}` : API_PATHS.PLANNER_ACCESS;
      },
      providesTags: ['PlannerAccess'],
    }),

    getPlannerAccessById: builder.query<ApiResponse<PlannerBuildingAccess>, PlannerAccessId>({
      query: (id) => `${API_PATHS.PLANNER_ACCESS}/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'PlannerAccess', id }],
    }),

    getPlannersByBuilding: builder.mutation<
      ApiResponse<{ building: string; planners: { email: string; displayName: string }[] }[]>,
      { buildingIds: string[] }
    >({
      query: ({ buildingIds }) => ({
        url: API_PATHS.PLANNERS_BY_BUILDING,
        method: 'POST',
        body: { buildingIds },
      }),
    }),

    getPlannerBuildings: builder.query<{ buildingIds: string[]; buildings: Building[] }, void>({
      serializeQueryArgs: ({ endpointName }) => {
        const plannerEmail = getActiveAccount()?.username ?? 'anonymous';
        return `${endpointName}(${plannerEmail})`;
      },
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        const plannerEmail = getActiveAccount()?.username;
        if (!plannerEmail) {
          return { data: { buildingIds: [], buildings: [] } };
        }
        const result = await baseQuery({
          url: API_PATHS.PLANNER_BUILDING_ACCESS,
          method: 'POST',
          body: { plannerEmail },
        });
        if (result.error) {
          return { data: { buildingIds: [], buildings: [] } };
        }
        const response = result.data as {
          buildings?: Building[];
        };
        const blds = response.buildings ?? [];
        const ids = blds.map((b) => b.buildingId);
        return { data: { buildingIds: ids, buildings: blds } };
      },
      providesTags: ['PlannerAccess'],
      keepUnusedDataFor: 600,
    }),
  }),
});

export const {
  useGetSpacesQuery,
  useLazyGetSpacesQuery,
  useSearchSpacesQuery,
  useLazySearchSpacesQuery,
  useSearchSpacesAvailabilityMutation,
  useCheckSpaceAvailabilityMutation,
  useGetCountryCodesQuery,
  useGetBuildingsQuery,
  useGetBuildingByIdQuery,
  useGetRegionConfigQuery,
  useGetPlannerAccessQuery,
  useGetPlannerAccessByIdQuery,
  useGetPlannersByBuildingMutation,
  useGetPlannerBuildingsQuery,
} = spaceConfigServiceApi;
